import type React from 'react'
import { useState } from 'react'
import { BookOpen, Columns2, Film, Images } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ScreenplayScene, StoryboardPanel } from '@/api/multimodalApi'
import { multimodalApi, parsePanelPayload } from '@/api/multimodalApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Asset } from '@/types/nf'

export type ReaderTab = 'text' | 'comic' | 'video'

const readerTabs = [
  { key: 'text', icon: BookOpen },
  { key: 'comic', icon: Images },
  { key: 'video', icon: Film },
] as const

const PAGE_SIZE = 12

export function normalizeReaderTab(value: string | null | undefined): ReaderTab {
  const raw = value ?? ''
  const search = raw.startsWith('?') ? raw : raw.includes('?') ? raw.slice(raw.indexOf('?')) : ''
  if (search) {
    const query = search.split('#', 1)[0]
    const tab = new URLSearchParams(query).get('tab')
    if (tab === 'comic' || tab === 'video' || tab === 'text') return tab
  }
  const hash = raw.includes('#') ? raw.slice(raw.lastIndexOf('#') + 1) : raw.replace(/^#/, '')
  if (hash === 'comic' || hash === 'video' || hash === 'text') return hash
  return 'text'
}

export function readerTabHref(currentPath: string, tab: ReaderTab): string {
  const [pathWithSearch] = currentPath.split('#', 1)
  return `${pathWithSearch}#${tab}`
}

export function latestStoryboardAsset(chapterId: string | null | undefined, assets: Asset[]): Asset | undefined {
  return assets
    .filter((asset) => asset.asset_type === 'storyboard' && (!chapterId || asset.chapter_id === chapterId))
    .sort((a, b) => b.version - a.version || String(b.updated_at).localeCompare(String(a.updated_at)))[0]
}

function readString(record: Record<string, unknown> | null | undefined, fields: string[]) {
  for (const field of fields) {
    const value = record?.[field]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function sceneTitle(scene: ScreenplayScene, index: number, fallback: string) {
  return scene.scene_id?.trim() || fallback.replace('{{index}}', String(index + 1))
}

export function ReaderTabBar({
  activeTab,
  currentPath,
  onTabChange,
}: {
  activeTab: ReaderTab
  currentPath: string
  onTabChange: (tab: ReaderTab) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="border-b bg-background">
      <div className="mx-auto flex w-full max-w-[760px] px-4 py-3">
        <div className="inline-flex rounded-lg bg-muted p-1" role="tablist" aria-label={t('reader.tab.label')}>
          {readerTabs.map(({ key, icon: Icon }) => (
            <Button
              key={key}
              asChild
              variant={activeTab === key ? 'secondary' : 'ghost'}
              size="sm"
              className={cn('h-8 gap-1.5 rounded-md px-3', activeTab === key ? 'shadow-sm' : 'text-muted-foreground')}
            >
              <a
                href={readerTabHref(currentPath, key)}
                role="tab"
                aria-selected={activeTab === key}
                onClick={() => onTabChange(key)}
              >
                <Icon className="size-4" />
                {t(`reader.tab.${key}`)}
              </a>
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}

export function EmptyMultimodalState({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-[calc(100vh-11rem)] w-full max-w-[760px] items-center justify-center px-4 py-16">
      <div className="rounded-md border border-dashed bg-muted/20 px-6 py-8 text-center text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  )
}

export function ReaderMultimodalLoading() {
  const { t } = useTranslation()
  return (
    <div className="mx-auto w-full max-w-[760px] px-4 py-10">
      <div className="rounded-md border border-dashed bg-muted/20 px-6 py-8 text-center text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    </div>
  )
}

export function ReaderMultimodalError({ message }: { message?: string }) {
  const { t } = useTranslation()
  return (
    <EmptyMultimodalState>
      <p className="font-medium text-foreground">{t('reader.loadFailed')}</p>
      {message ? <p className="mt-2">{message}</p> : null}
    </EmptyMultimodalState>
  )
}

export function ScreenplayReaderView({
  scenes,
  onJumpToPanel,
}: {
  scenes: ScreenplayScene[]
  onJumpToPanel?: (sceneId: string) => void
}) {
  const { t } = useTranslation()
  if (!scenes.length) return <EmptyMultimodalState>{t('reader.emptyScript')}</EmptyMultimodalState>

  return (
    <article className="mx-auto w-full max-w-[760px] px-5 py-8 sm:px-6 sm:py-10">
      <div className="space-y-10 font-serif">
        {scenes.map((scene, index) => {
          const scriptSceneId = scene.scene_id?.trim()
          return (
            <section key={scriptSceneId || index} id={scriptSceneId ? `scene-${scriptSceneId}` : undefined} className="space-y-4">
              <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold uppercase tracking-normal text-foreground">
                    {sceneTitle(scene, index, t('reader.script.sceneFallback'))}
                  </p>
                  {scene.setting ? <p className="text-sm text-muted-foreground">{scene.setting}</p> : null}
                </div>
                {scriptSceneId && onJumpToPanel ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 self-start gap-1.5 rounded-md px-2.5 font-sans text-xs"
                    aria-label={t('reader.script.jumpToStoryboardScene', { scene: scriptSceneId })}
                    onClick={() => onJumpToPanel(scriptSceneId)}
                  >
                    <Columns2 className="size-3.5" />
                    {t('reader.script.jumpToStoryboard')}
                  </Button>
                ) : null}
              </header>
              {scene.action ? <p className="leading-8 text-foreground">{scene.action}</p> : null}
              {scene.dialogues?.length ? (
                <div className="space-y-4 pl-6 sm:pl-12">
                  {scene.dialogues.map((dialogue, dialogueIndex) => {
                    const record = dialogue as Record<string, unknown>
                    const character = readString(record, ['character', '角色']) || t('reader.script.unknownSpeaker')
                    const content = readString(record, ['content', 'line', '台词']) || ''
                    const mood = readString(record, ['emotion', 'mood', 'bubble_type', '情绪'])
                    return (
                      <div key={`${character}-${dialogueIndex}`} className="max-w-[34em]">
                        <p className="text-sm font-semibold uppercase tracking-normal">
                          {character}
                          {mood ? <span className="ml-2 font-normal text-muted-foreground">({mood})</span> : null}
                        </p>
                        {content ? <p className="mt-1 leading-8">{content}</p> : null}
                      </div>
                    )
                  })}
                </div>
              ) : null}
              {scene.narration ? <p className="border-l-2 pl-4 leading-8 text-muted-foreground">{scene.narration}</p> : null}
            </section>
          )
        })}
      </div>
    </article>
  )
}

function storyboardField(panel: StoryboardPanel, fields: string[]) {
  return readString(parsePanelPayload(panel), fields)
}

function dialogueBubbles(panel: StoryboardPanel) {
  const payload = parsePanelPayload(panel)
  if (!Array.isArray(payload?.dialogue_bubbles)) return []
  return payload.dialogue_bubbles
    .map((bubble) => {
      if (!bubble || typeof bubble !== 'object') return undefined
      const record = bubble as Record<string, unknown>
      const character = readString(record, ['character', '角色'])
      const content = readString(record, ['content', '台词'])
      if (!content) return undefined
      return character ? `${character}: ${content}` : content
    })
    .filter((bubble): bubble is string => Boolean(bubble))
}

// PanelImageSlot 尝试加载该镜头的出图; 无图/加载失败回落"出图待生成"占位
// (#266 Phase2: 图片已落盘时直接显, gated 的只是尚未出图的镜头)。
function PanelImageSlot({ panelId }: { panelId: string }) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)
  const slotClass = 'flex aspect-video items-center justify-center overflow-hidden rounded-md border bg-muted/30 sm:aspect-[4/3]'
  if (failed || !panelId) {
    return (
      <div className={cn(slotClass, 'border-dashed text-sm text-muted-foreground')}>
        {t('reader.storyboard.imagePending')}
      </div>
    )
  }
  return (
    <div className={slotClass}>
      <img
        src={multimodalApi.panelImageUrl(panelId)}
        alt={t('reader.storyboard.imageAlt')}
        loading="lazy"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  )
}

export function StoryboardReaderView({
  panels,
  onJumpToScene,
}: {
  panels: StoryboardPanel[]
  onJumpToScene?: (sceneId: string) => void
}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  if (!panels.length) return <EmptyMultimodalState>{t('reader.emptyStoryboard')}</EmptyMultimodalState>
  const pageCount = Math.max(1, Math.ceil(panels.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const visiblePanels = panels.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)
  const anchoredScenes = new Set<string>()

  return (
    <div className="mx-auto w-full max-w-[860px] space-y-4 px-4 py-8 sm:py-10">
      {visiblePanels.map((panel) => {
        const shotType = storyboardField(panel, ['shot_type', '景别'])
        const sceneId = storyboardField(panel, ['scene_id'])
        const composition = storyboardField(panel, ['composition', '构图', 'action'])
        const narration = storyboardField(panel, ['narration_box'])
        const transition = storyboardField(panel, ['transition'])
        const bubbles = dialogueBubbles(panel)
        const panelSceneAnchor = sceneId && !anchoredScenes.has(sceneId) ? `panel-scene-${sceneId}` : undefined
        if (sceneId && panelSceneAnchor) anchoredScenes.add(sceneId)
        return (
          <article
            key={panel.id}
            id={panelSceneAnchor}
            className="grid gap-4 rounded-lg border bg-background p-4 shadow-sm sm:grid-cols-[220px_1fr]"
          >
            <PanelImageSlot panelId={panel.id} />
            <div className="min-w-0 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold">{t('reader.storyboard.panelNo', { number: panel.panel_sequence })}</h2>
                {shotType ? (
                  <Badge variant="secondary" className="rounded-md">
                    {shotType}
                  </Badge>
                ) : null}
                {sceneId ? (
                  onJumpToScene ? (
                    <Badge asChild variant="outline" className="rounded-md">
                      <button
                        type="button"
                        aria-label={t('reader.storyboard.jumpToScene', { scene: sceneId })}
                        onClick={() => onJumpToScene(sceneId)}
                      >
                        {sceneId}
                      </button>
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-md">
                      {sceneId}
                    </Badge>
                  )
                ) : null}
              </div>
              {composition ? <p className="leading-7 text-foreground">{composition}</p> : null}
              {bubbles.length ? (
                <div className="flex flex-wrap gap-2">
                  {bubbles.map((bubble, index) => (
                    <Badge key={`${panel.id}-bubble-${index}`} variant="outline" className="rounded-md text-xs font-normal">
                      {bubble}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {narration ? <p className="border-l-2 pl-3 text-sm leading-6 text-muted-foreground">{narration}</p> : null}
              {transition ? (
                <p className="text-xs font-medium uppercase tracking-normal text-muted-foreground">
                  {t('reader.storyboard.transition')}: {transition}
                </p>
              ) : null}
            </div>
          </article>
        )
      })}
      {pageCount > 1 ? (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Button type="button" variant="outline" size="sm" disabled={safePage <= 0} onClick={() => setPage(safePage - 1)}>
            {t('pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('pagination.pageWithUnit', { page: safePage + 1, total: pageCount })}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('pagination.totalImages', { total: panels.length })}
          </span>
          <Button type="button" variant="outline" size="sm" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
            {t('pagination.next')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
